// Premium Economy assets. Single source of truth for PE image URLs.
// Pattern URLs match flights-pwa (`peAssets.ts`, `fareCards.ts`, itinerary SCSS).
export const RUKMINI_IMAGE_BASE_URL =
    'https://rukmini-ct.flixcart.com/f_auto,q_auto/offermgmt-prod/offermgmt/images'

/** Interstitial / fare-card banners (under offermgmt/images/premiumEconomy). */
const PE_IMAGE_DIR = `${RUKMINI_IMAGE_BASE_URL}/premiumEconomy`

/**
 * Mobile fareClassTheme patterns live under `images/premium_economy` (underscore),
 * not `premiumEconomy`. Keep that path — different folder from PE_IMAGE_DIR.
 */
const PE_MOBILE_THEME_DIR = `${RUKMINI_IMAGE_BASE_URL}/premium_economy`

/** SRP header PNGs (under offermgmt/premiumEconomy) — same as flights-pwa. */
export const PE_SRP_CDN_BASE =
    'https://rukmini-ct.flixcart.com/f_auto,q_auto/offermgmt-prod/offermgmt/premiumEconomy'

/** SRP header SVG + videos (Rukmini is image-only) — same as flights-pwa. */
export const PE_STATIC_ASSETS_BASE =
    'https://static-assets-ct.flixcart.com/ct/assets/offermgmt/premiumEconomy'

const PE_HEADER_VIDEO_DIR = `${PE_STATIC_ASSETS_BASE}/headerAssets`

/**
 * SRP hero title wordmarks (CDN SVG). Used instead of live text so designed
 * glyphs stay identical across devices.
 */
export const PE_SRP_HERO_TITLE_IMAGES = {
    premium: `${PE_HEADER_VIDEO_DIR}/Premium/Premium_DT_Title.svg`,
    business: `${PE_HEADER_VIDEO_DIR}/Business/Business_DT_Title.svg`,
    first: `${PE_HEADER_VIDEO_DIR}/First/First_DT_Title.svg`,
} as const

export const PE_ASSETS = {
    // Hero images from CDN.
    srpHeroSeat: `${PE_IMAGE_DIR}/pe-srp-hero-seat.png`,
    srpHeroFirst: `${PE_IMAGE_DIR}/pe-srp-hero-first.png`,

    // SRP header patterns — flights-pwa peAssets / header SCSS.
    srpPatternGold: `${PE_SRP_CDN_BASE}/pe-header-pattern.png`,
    srpPatternFirst: `${PE_STATIC_ASSETS_BASE}/first-class-pattern.svg`,

    // Interstitial header patterns — flights-pwa fareClassTheme.interstitialHeader.
    interstitialPatternGold: `${PE_MOBILE_THEME_DIR}/premiumEconomyInterstitialPattern.png`,
    interstitialPatternFirst: `${PE_MOBILE_THEME_DIR}/firstClassInterstitialPattern.png`,

    // Itinerary header patterns — flights-pwa premiumEconomyItinerary.scss.
    itineraryPatternGold: `${PE_IMAGE_DIR}/pe-itinerary-gold-pattern.png`,
    itineraryPatternFirst: `${PE_MOBILE_THEME_DIR}/firstClassInterstitialPattern.png`,

    // Selected fare-card corner patterns — flights-pwa fareClassTheme.fareCard.
    cardPatternGold: `${PE_MOBILE_THEME_DIR}/premiumEconomyCardPattern.png`,
    cardPatternFirst: `${PE_MOBILE_THEME_DIR}/firstClassCardPattern.png`,

    // Aliases kept for older call sites (promo tile / experience card).
    headerPattern: `${PE_SRP_CDN_BASE}/pe-header-pattern.png`,
    firstClassPattern: `${PE_STATIC_ASSETS_BASE}/first-class-pattern.svg`,
    promo: `${PE_IMAGE_DIR}/pe-promo.png`,
} as const

/**
 * SRP hero cabin videos (Premium Economy / Business / First).
 * The cabin's hero image (see `PE_ASSETS.srpHero*`) paints instantly as the loading
 * fallback; this muted, looping video is lazily mounted after first paint and
 * cross-fades in over the image so it never blocks the initial render.
 */
export const CABIN_HERO_VIDEOS = {
    premium: `${PE_HEADER_VIDEO_DIR}/Premium/PremiumEconomy.mp4`,
    business: `${PE_HEADER_VIDEO_DIR}/Business/Business.mp4`,
    first: `${PE_HEADER_VIDEO_DIR}/First/First.mp4`,
} as const

// Carousel PNGs live under offermgmt/premiumEconomy (NOT images/) — a different
// Rukmini path than PE_ASSETS. Kept in sync with the flights-pwa interstitial carousel.
const PE_CAROUSEL_DIR = `${PE_SRP_CDN_BASE}/carouselImages`

const buildCarousel = (folder: string, count = 3): string[] =>
    Array.from({ length: count }, (_, i) => `${PE_CAROUSEL_DIR}/${folder}/${i + 1}.png`)

/**
 * Interstitial cabin-image carousel (Premium Economy / Business / First).
 * Each cabin cross-fades through its own set of cabin photos; on load error a slide
 * falls back to the single promo image.
 */
export const FARE_CLASS_CAROUSEL_IMAGES: Record<
    'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST',
    string[]
> = {
    PREMIUM_ECONOMY: buildCarousel('premiumEconomy'),
    BUSINESS: buildCarousel('business'),
    // First-class assets pending — reuse Premium Economy carousel until BE/design provides them.
    FIRST: buildCarousel('firstClass'),
}
